/* eslint-disable @typescript-eslint/no-explicit-any */
import { Form, Row, Col } from 'react-bootstrap';
import {
  useForm,
  FieldValues,
  SubmitHandler,
  Controller
} from 'react-hook-form';
import { useDispatch } from 'react-redux';
import { Dispatch } from 'redux';
import { useQuery } from 'react-query';
import { useState, useRef, useEffect } from 'react';
import { useSelector } from 'react-redux';
import { useTranslation } from 'react-i18next';
import { useLocation } from 'react-router-dom';

import usePageSearchParams from 'utils/usePageSearchParams';
import legalEntityCurrencyService from 'service/legalEntityCurrency';
import legalEntityCurrencyRateService from 'service/legalEntityCurrencyRate';
import Button from 'components/base/Button';
import { showAlert } from 'store/alert/alert.thunk';
import walletService from 'service/wallet';
import providerService from 'service/provider';
import paymentOrderService from 'service/paymentOrder';

interface AddSettelementProps {
  setOpenSettelement: (show: boolean) => void;
  refetch?: any;
  totalRemainder?: any;
  setTotalRemainder?: any;
}

const formatNumber = (value: string) => {
  return value?.replace(/\B(?=(\d{3})+(?!\d))/g, ' ');
};

const removeSpaces = (value: string) => {
  return value?.replace(/\s/g, '');
};

const AddSettelement = ({
  setOpenSettelement,
  refetch,
  totalRemainder,
  setTotalRemainder
}: AddSettelementProps) => {
  const { t, i18n } = useTranslation();

  const {
    register,
    handleSubmit,
    control,
    watch,
    setValue,
    formState: { errors }
  } = useForm({});
  const { pathname, state } = useLocation();
  const { creditor_pid, currency_pid } = state;
  const [searchParams] = usePageSearchParams();
  const dispatch: Dispatch<any> = useDispatch();
  const inputRef = useRef<HTMLInputElement | any>();
  const cashboxId = useSelector((state: any) => state?.shiftId?.shiftId);
  const cashboxPid = useSelector((state: any) => state?.shiftId?.shiftPid);
  const creditor_id = pathname.split('/').pop();
  const debitor_id = searchParams.get('debitor_id');
  const currency_id = searchParams.get('currency_id');

  const [loading, setLoading] = useState(false);
  const [walletOption, setWalletOption] = useState<any>([
    { label: '', value: '', total_balance: '' }
  ]);
  const [disabledFields, setDisabledFields] = useState(false);
  const [legalEntityCurrencyOption, setLegalEntityCrrencyOption] =
    useState<any>([]);
  const [selectedCurrencyId, setSelectedCurrencyId] = useState<string>('');
  const [selectedCurrencyPid, setSelectedCurrencyPid] = useState<number>(0);
  const [lastRateValue, setLastRateValue] = useState<number>(1);
  const [paymentOrderOption, setPaymentOrderOption] = useState<any>([]);
  const [selectedPaymentMethod, setSelectedPaymentMethod] = useState<any>();

  const debitValue = removeSpaces(watch('debit') ?? '');

  //GET Wallet to options
  useQuery(
    ['GET_WALLET', selectedCurrencyId],
    async () => {
      if (selectedCurrencyId !== '') {
        await walletService
          .getList({
            offset: 0,
            limit: 20,
            currency_id: selectedCurrencyId
          })
          .then((res: any) => {
            const options = res?.wallets?.map((option: any) => ({
              value: option.id,
              pid: option?.pid || 0,
              label: option.name,
              total_balance: option?.total_balance
            }));
            setWalletOption(options);
          });
      }
    },
    { enabled: !!selectedCurrencyId }
  );

  //GET currency to options
  useQuery(['GET_LEGAL_ENTITY_CURRENCY'], async () => {
    await legalEntityCurrencyService
      .getList({ offset: 0, limit: 100, status: true })
      .then((res: any) => {
        const options = res?.legal_entity_currencies?.map((option: any) => ({
          value: option?.currency?.id,
          pid: option?.currency?.pid || 0,
          label: option?.currency?.name?.[i18n?.language]
        }));

        setLegalEntityCrrencyOption(options);
      });
  });

  useQuery(['GET_LEGAL_ENTITY_CURRENCY_RATE', selectedCurrencyId], async () => {
    if (selectedCurrencyId !== '') {
      await legalEntityCurrencyRateService
        .getListConversion({
          offset: 0,
          limit: 100,
          income_currency_id: selectedCurrencyId,
          outcome_currency_id: currency_id
        })
        .then((res: any) => {
          if (res) {
            setLastRateValue(
              res?.legal_entity_currency_conversion_rates?.[0]
                ?.outcome_amount || 0
            );
          }
        });
    }
  });

  //GET Payment order to options
  useQuery(['GET_PAYMENT_ORDER'], async () => {
    await paymentOrderService
      .getListPaymentType({ offset: 0, limit: 100, status: true })
      .then((res: any) => {
        const options = res?.payment_types
          ?.filter((items: any) => items?.pid !== 3)
          ?.map((option: any) => ({
            label: option?.name?.[i18n?.language],
            value: option?.pid || 0
          }));
        setPaymentOrderOption(options);
      });
  });

  const onSubmit: SubmitHandler<FieldValues> = data => {
    createData(data);
  };

  const createData = (data: any) => {
    const balance = +removeSpaces(data?.balance);
    const debit = +removeSpaces(data?.debit);
    if (
      (balance === 0 || balance < debit) &&
      selectedPaymentMethod?.value === 1
    ) {
      setLoading(false);
      dispatch(
        showAlert({
          title: t('not_enough_product')
        })
      );
      return;
    }

    if (+removeSpaces(totalRemainder) < debit) {
      setLoading(false);
      dispatch(
        showAlert({
          title: t('amount_exceed')
        })
      );
      return;
    }

    if (!cashboxId) {
      dispatch(showAlert({ title: t('shift_not_open') }));
      return;
    }

    const createdData = {
      currency_id: currency_id,
      currency_pid: currency_pid,
      creditor_pid: creditor_pid,
      cashbox_id: cashboxId,
      cashbox_pid: cashboxPid,
      amount:
        selectedCurrencyId !== currency_id ? lastRateValue * debit : debit,
      creditor_id: creditor_id,
      debitor_id: debitor_id,
      note: data?.note,
      wallet_id: data?.wallet,
      currency_rate: lastRateValue ?? 1,
      original_amount: debit,
      original_currency_id:
        selectedCurrencyId !== '' ? selectedCurrencyId : currency_id,
      original_currency_pid:
        selectedCurrencyPid !== 0 ? selectedCurrencyPid : currency_pid,
      payment_type_pid: +data?.payment_order_pid,
      provider_name: data?.provider_name,
      provider_indentification_number: +data?.provider_indentification_number
    };

    setLoading(true);

    providerService
      .createProviderSettlement(createdData)
      .then((res: any) => {
        if (res) {
          dispatch(
            showAlert({
              title: `${t('mutual_settlement')} ${t('successfully_added')}`,
              type: 'success'
            })
          );
          const updatedRemainder = +removeSpaces(totalRemainder) - debit;
          refetch();
          setOpenSettelement(false);
          setLoading(false);
          setTotalRemainder(
            new Intl.NumberFormat('en-US', {
              minimumFractionDigits: 0,
              maximumFractionDigits: 4,
              useGrouping: true
            })
              .format(updatedRemainder)
              .replace(/,/g, ' ')
          );
        }
      })
      .catch((error: any) => {
        console.log(error);
        setLoading(false);
      });
  };

  useEffect(() => {
    if (disabledFields && inputRef.current) {
      inputRef.current.focus();
    } else {
      if (inputRef.current) {
        inputRef.current.focus();
      }
    }
  }, [inputRef.current, disabledFields]);

  useEffect(() => {
    if (currency_id) {
      setValue('legal_entity_currency_id', currency_id);
      setSelectedCurrencyId(currency_id);
      setSelectedCurrencyPid(currency_pid);
    }
  }, [currency_id, currency_pid]);

  useEffect(() => {
    if (debitValue) {
      const updateRate = +debitValue * lastRateValue;
      setValue(
        'rate',
        new Intl.NumberFormat('en-US', {
          minimumFractionDigits: 0,
          maximumFractionDigits: 4,
          useGrouping: true
        })
          .format(updateRate)
          .replace(/,/g, ' ')
      );
    } else {
      setValue('rate', '1');
    }
  }, [debitValue]);

  return (
    <>
      <Form
        noValidate
        onSubmit={handleSubmit(onSubmit)}
        style={{ display: 'flex', flexDirection: 'column', height: '100%' }}
      >
        <Row className="mb-2">
          <Col>
            <Form.Floating>
              <Form.Control
                defaultValue={totalRemainder}
                disabled
                type="text"
                placeholder={t('remainder')}
              />
              <label htmlFor="floatingInputCustom">{t('remainder')}</label>
            </Form.Floating>
          </Col>
        </Row>

        <Form.Group className="md-4 mb-2">
          <div className="react-select-container">
            <Controller
              name="legal_entity_currency_id"
              control={control}
              defaultValue=""
              rules={{
                required: true
              }}
              render={({ field }) => (
                <Form.Floating>
                  <Form.Select
                    {...field}
                    onChange={(e: any) => {
                      const selectedOption = legalEntityCurrencyOption?.find(
                        (option: any) => option?.value === e?.target?.value
                      );

                      if (selectedOption) {
                        setSelectedCurrencyId(selectedOption?.value);
                        setSelectedCurrencyPid(selectedOption?.pid);
                        setValue('rate', '1');
                        setValue('wallet', '');
                        setValue('balance', '');
                        setValue('debit', '');
                        setValue('note', '');
                      }

                      field.onChange(e);
                    }}
                    isInvalid={!!errors.legal_entity_currency_id}
                  >
                    <option className="d-none" value=""></option>
                    {legalEntityCurrencyOption?.map((option: any) => (
                      <option
                        key={option.value}
                        value={option.value}
                        className="option"
                      >
                        {option.label}
                      </option>
                    ))}
                  </Form.Select>
                  <label htmlFor="floatingInputCustom">{t('currency')}</label>
                  {errors?.legal_entity_currency_id && (
                    <span
                      style={{
                        marginTop: '2px',
                        color: 'red',
                        fontSize: '12px'
                      }}
                    >
                      {t('required_field')}
                    </span>
                  )}
                </Form.Floating>
              )}
            />
          </div>
        </Form.Group>

        <Row className="mb-2">
          <Col>
            <div className="react-select-container">
              <Controller
                name="payment_order_pid"
                control={control}
                defaultValue=""
                rules={{
                  required: true
                }}
                render={({ field }) => (
                  <Form.Floating>
                    <Form.Select
                      {...field}
                      onChange={(e: any) => {
                        const selectedOption = paymentOrderOption.find(
                          (option: any) =>
                            Number(option.value) === Number(e.target.value)
                        );
                        if (selectedOption) {
                          setSelectedPaymentMethod(selectedOption);
                        }
                        field.onChange(e);
                      }}
                      isInvalid={!!errors.payment_order_pid}
                    >
                      <option className="d-none" value=""></option>
                      {paymentOrderOption?.map((option: any) => (
                        <option key={option.value} value={option.value}>
                          {option.label}
                        </option>
                      ))}
                    </Form.Select>

                    <label htmlFor="floatingInputCustom">
                      {t('payment_procedure')}
                    </label>

                    {errors?.payment_order_pid && (
                      <span
                        style={{
                          marginTop: '2px',
                          color: 'red',
                          fontSize: '12px'
                        }}
                      >
                        {t('required_field')}
                      </span>
                    )}
                  </Form.Floating>
                )}
              />
            </div>
          </Col>
        </Row>

        {selectedPaymentMethod?.value === 1 && (
          <Row className="mb-2">
            <Col>
              <div className="react-select-container">
                <Controller
                  name="wallet"
                  control={control}
                  defaultValue=""
                  rules={{
                    required: true
                  }}
                  render={({ field }) => (
                    <Form.Floating>
                      <Form.Select
                        {...field}
                        onChange={(e: any) => {
                          const selectedOption = walletOption.find(
                            (option: any) => option.value === e.target.value
                          );
                          if (selectedOption) {
                            setValue(
                              'balance',
                              selectedOption.total_balance
                                ? new Intl.NumberFormat('en-US', {
                                    minimumFractionDigits: 0,
                                    maximumFractionDigits: 4,
                                    useGrouping: true
                                  })
                                    .format(selectedOption.total_balance)
                                    .replace(/,/g, ' ')
                                : '0'
                            );

                            setDisabledFields(true);
                          }
                          field.onChange(e);
                        }}
                        isInvalid={!!errors.wallet}
                      >
                        <option className="d-none" value=""></option>
                        {walletOption?.map((option: any) => (
                          <option key={option.value} value={option.value}>
                            {option.label}
                          </option>
                        ))}
                      </Form.Select>

                      <label htmlFor="floatingInputCustom">{t('wallet')}</label>

                      {errors?.wallet && (
                        <span
                          style={{
                            marginTop: '2px',
                            color: 'red',
                            fontSize: '12px'
                          }}
                        >
                          {t('required_field')}
                        </span>
                      )}
                    </Form.Floating>
                  )}
                />
              </div>
            </Col>
          </Row>
        )}

        {selectedPaymentMethod?.value === 1 && (
          <Row className="mb-2">
            <Col>
              <Form.Floating>
                <Form.Control
                  disabled={disabledFields}
                  {...register('balance')}
                  type="text"
                  placeholder={t('balance')}
                  onFocus={e => e.target.select()}
                />
                <label htmlFor="floatingInputCustom">{t('balance')}</label>
              </Form.Floating>
            </Col>
          </Row>
        )}

        {selectedPaymentMethod?.value === 2 && (
          <Row className="mb-2">
            <Col>
              <Controller
                name="provider_name"
                control={control}
                rules={{ required: true }}
                render={({ field }) => (
                  <Form.Floating>
                    <Form.Control
                      {...field}
                      type="text"
                      placeholder={t('firm_name')}
                      onFocus={e => e.target.select()}
                      isInvalid={!!errors.provider_name}
                    />
                    <label htmlFor="floatingInputCustom">
                      {t('firm_name')}
                    </label>

                    {errors?.provider_name && (
                      <span
                        style={{
                          marginTop: '2px',
                          color: 'red',
                          fontSize: '12px'
                        }}
                      >
                        {t('required_field')}
                      </span>
                    )}
                  </Form.Floating>
                )}
              />
            </Col>
          </Row>
        )}
        {selectedPaymentMethod?.value === 2 && (
          <Row className="mb-2">
            <Col>
              <Controller
                name="provider_indentification_number"
                control={control}
                rules={{ required: true }}
                render={({ field }) => (
                  <Form.Floating>
                    <Form.Control
                      {...field}
                      type="text"
                      inputMode="numeric"
                      placeholder={t('firm_pnfl')}
                      onFocus={e => e.target.select()}
                      isInvalid={!!errors.provider_indentification_number}
                      maxLength={9}
                      onKeyDown={e => {
                        if (
                          e.key === 'Backspace' ||
                          e.key === 'Delete' ||
                          e.key === 'ArrowLeft' ||
                          e.key === 'ArrowRight' ||
                          e.key === 'Tab' ||
                          (e.ctrlKey && e.key === 'a') // Allow Ctrl+A for select all
                        ) {
                          return; // Allow these keys
                        }
                        // Allow only numeric input
                        if (!/[0-9]/.test(e.key)) {
                          e.preventDefault(); // Block non-numeric keys
                        }
                      }}
                    />
                    <label htmlFor="floatingInputCustom">
                      {t('firm_pnfl')}
                    </label>

                    {errors?.provider_indentification_number && (
                      <span
                        style={{
                          marginTop: '2px',
                          color: 'red',
                          fontSize: '12px'
                        }}
                      >
                        {t('required_field')}
                      </span>
                    )}
                  </Form.Floating>
                )}
              />
            </Col>
          </Row>
        )}

        <Row className="mb-2">
          <Col>
            <Controller
              name="debit"
              control={control}
              rules={{ required: true }}
              render={({ field }) => (
                <Form.Floating>
                  <Form.Control
                    {...field}
                    ref={disabledFields === true ? inputRef : undefined}
                    type="text"
                    placeholder={t('debit')}
                    value={field.value || ''}
                    onFocus={e => {
                      if (field.value && field.value.startsWith('-')) {
                        // Remove the minus sign if present
                        const sanitizedValue = field.value.replace('-', '');
                        field.onChange(sanitizedValue);
                      }
                      e.target.select();
                    }}
                    onChange={e => {
                      const rawValue = removeSpaces(e.target.value) || '';

                      const remainderPattern = /^\d*\.?\d*$/;

                      if (remainderPattern.test(rawValue)) {
                        const formattedValue = formatNumber(rawValue);
                        field.onChange(formattedValue);
                      }
                    }}
                    isInvalid={!!errors.debit}
                  />
                  <label htmlFor="floatingInputCustom">{t('debit')}</label>

                  {errors?.debit && (
                    <span
                      style={{
                        marginTop: '2px',
                        color: 'red',
                        fontSize: '12px'
                      }}
                    >
                      {t('required_field')}
                    </span>
                  )}
                </Form.Floating>
              )}
            />
          </Col>
        </Row>

        <Row className="mb-2">
          <Col>
            <Form.Floating>
              <Form.Control
                {...register('note')}
                type="text"
                placeholder={t('note')}
                onFocus={e => e.target.select()}
              />
              <label htmlFor="floatingInputCustom">{t('note')}</label>
            </Form.Floating>
          </Col>
        </Row>

        <Button
          style={{ height: '48px', marginTop: 'auto' }}
          variant="primary"
          type="submit"
          loading={loading}
        >
          {t('save')}
        </Button>
      </Form>
    </>
  );
};

export default AddSettelement;
